import { LitElement, html, svg, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";

export type SparklineDatum = {
  label: string;
  value: number;
  secondary?: number;
  tertiary?: number;
};

/**
 * <ig-sparkline> — a lightweight SVG bar chart for the Overview dashboard.
 *
 * Supports up to 3 stacked series (e.g. input/output/cache tokens).
 * Renders inline SVG with hover tooltips. Light-DOM styling via CSS tokens.
 */
@customElement("ig-sparkline")
export class Sparkline extends LitElement {
  protected createRenderRoot() {
    return this;
  }

  @property({ type: Array }) data: SparklineDatum[] = [];
  @property({ type: String }) mode: "bar" | "line" = "bar";
  @property({ type: String }) color = "var(--accent, #4a9)";
  @property({ type: String }) secondaryColor = "var(--info, #59c)";
  @property({ type: String }) tertiaryColor = "var(--warn, #c94)";
  @property({ type: Number }) height = 60;
  @property({ type: String }) unit = "";
  @property({ type: String }) format: "compact" | "raw" = "compact";

  private formatValue(n: number): string {
    if (this.format === "raw") return String(n);
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return String(Math.round(n));
  }

  render() {
    if (!this.data.length) {
      return html`<div class="sparkline-empty" style="height:${this.height}px;display:flex;align-items:center;justify-content:center;color:var(--text-muted,#888);font-size:0.8em;">No data</div>`;
    }

    const maxVal = Math.max(...this.data.map((d) => d.value + (d.secondary ?? 0) + (d.tertiary ?? 0)), 1);
    const barCount = this.data.length;
    const labelH = 16;
    const chartH = this.height - labelH;
    // Work in a 100-wide viewBox so bars can be positioned with NUMERIC x/width
    // and the SVG stretches to the container. (SVG transforms don't accept % —
    // the previous translate(N%) was ignored and every bar rendered at x=0,
    // full width, overlapping.)
    const VBW = 100;
    const gap = barCount > 1 ? 1.5 : 0;
    const barW = (VBW - gap * (barCount - 1)) / barCount;

    return html`
      <div class="sparkline" style="height:${this.height}px;">
        <svg
          width="100%"
          height=${chartH}
          viewBox="0 0 ${VBW} ${chartH}"
          preserveAspectRatio="none"
          style="display:block;"
        >
          ${this.data.map((d, i) => {
            const total = d.value + (d.secondary ?? 0) + (d.tertiary ?? 0);

            const primaryH = (d.value / maxVal) * chartH;
            const secondaryH = ((d.secondary ?? 0) / maxVal) * chartH;
            const tertiaryH = ((d.tertiary ?? 0) / maxVal) * chartH;

            const x = i * (barW + gap);
            const yPrimary = chartH - primaryH;
            const ySecondary = yPrimary - secondaryH;
            const yTertiary = ySecondary - tertiaryH;

            return svg`
              <g class="sparkline-bar-group">
                <title>${d.label}: ${this.formatValue(total)}${this.unit}</title>
                ${d.tertiary ? svg`<rect
                  x=${x} y=${yTertiary}
                  width=${barW} height=${tertiaryH}
                  fill=${this.tertiaryColor}
                  opacity="0.7"
                />` : nothing}
                ${d.secondary ? svg`<rect
                  x=${x} y=${ySecondary}
                  width=${barW} height=${secondaryH}
                  fill=${this.secondaryColor}
                  opacity="0.8"
                />` : nothing}
                <rect
                  x=${x} y=${yPrimary}
                  width=${barW} height=${primaryH}
                  fill=${this.color}
                />
              </g>
            `;
          })}
        </svg>
        <div class="sparkline-labels" style="display:flex;justify-content:space-between;font-size:0.7em;color:var(--text-muted,#888);margin-top:2px;">
          ${this.data.map((d) => html`<span>${d.label}</span>`)}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "ig-sparkline": Sparkline;
  }
}
